import { Service, signal } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { Logger } from '@app/core/logger.service';
import { OAuthErrorRedirect } from '../oauth-error-redirect';
import { clearTransientLoginKeys } from '../oauth-storage';

const _log = new Logger('AuthFailure');

/**
 * Terminal state for the OAuth bootstrap.
 *
 * Until this existed, a login that could not be completed produced no state at
 * all: the login chain rejected, nothing rendered, and the app sat on a blank
 * page indefinitely. The app shell now subscribes to `failed$` and shows a panel
 * the user can act on, and the AuthenticationGuard reports here instead of
 * hanging forever on `credentials$`.
 */
@Service()
export class AuthFailureService {
  private readonly failedSubject = new BehaviorSubject<boolean>(false);
  private readonly accessDeniedState = signal<OAuthErrorRedirect | null>(null);

  /**
   * Set when the identity provider refused the sign-in (`?error=access_denied`,
   * e.g. the account is not a member of this workspace). Restarting the code
   * flow cannot help then: the ID server session cookie is still there and
   * the answer would be the same, so the app shows a dedicated page instead.
   */
  readonly accessDenied = this.accessDeniedState.asReadonly();

  /** Emits `true` once the authentication bootstrap has definitively failed. */
  readonly failed$: Observable<boolean> = this.failedSubject.asObservable();

  get failed(): boolean {
    return this.failedSubject.value;
  }

  fail(reason?: unknown): void {
    if (this.failedSubject.value) {
      return;
    }

    _log.error('Authentication bootstrap failed, asking the user to sign in again', reason);
    this.failedSubject.next(true);
  }

  denyAccess(redirect: OAuthErrorRedirect): void {
    _log.warn(`The identity provider refused the sign-in: ${redirect.error} ${redirect.description ?? ''}`.trim());
    this.accessDeniedState.set(redirect);
  }

  reset(): void {
    if (this.failedSubject.value) {
      this.failedSubject.next(false);
    }
  }

  /**
   * Start over from a clean slate. The half-finished login state is what usually
   * broke, so drop it and reload — a full reload is the one path that cannot be
   * blocked by a suppressed `openUri` or an already-armed code flow.
   */
  retry(): void {
    clearTransientLoginKeys();
    window.location.reload();
  }
}
